/**
 * DeepTimeFormatter — Tiered time display for retreats spanning minutes to years.
 *
 * The "7-Year Auditor" engine (ObjectiveClock) produces elapsed seconds up to
 * 220,752,000 (7 years). This formatter converts that into human-readable
 * tiered display strings.
 *
 * Tiers:
 *   < 1 hour  :  "45:30"                   (mm:ss)
 *   < 1 day   :  "3:45:30"                 (h:mm:ss)
 *   < 1 year  :  "Day 14 · 3:45"           (Day N · h:mm)
 *   ≥ 1 year  :  "Year 3 · Day 142 · 3:45" (Year N · Day N · h:mm)
 *
 * Performance Target: 1,000,000 calls in < 100ms (called every tick at 60fps).
 * All math is integer-only (no floating point drift over long durations).
 *
 * RIGHT BICEP:
 *   [R] Correct output for all tiers
 *   [B] 0  "0:00", negative  "0:00", 220_752_000  "Year 7 · Day 365 · 0:00"
 *   [E] NaN/Infinity  "0:00"
 *   [P] Pure arithmetic, no allocations beyond the return string
 */

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3_600;
const SECONDS_PER_DAY = 86_400;
const SECONDS_PER_YEAR = 365 * SECONDS_PER_DAY; // 31,536,000

/**
 * Format elapsed seconds into a human-readable Deep Time string.
 *
 * @param totalSeconds - Elapsed time in seconds (from engine TICK/SYNC events).
 *                       Clamped to ≥ 0. Non-finite values treated as 0.
 * @returns Formatted string in the appropriate tier.
 *
 * @example
 * formatDeepTime(0)            "0:00"
 * formatDeepTime(3930)         "1:05:30"
 * formatDeepTime(90_000)       "Day 2 · 1:00"
 * formatDeepTime(220_752_000)  "Year 7 · Day 365 · 0:00"
 */
export function formatDeepTime(totalSeconds: number): string {
  // Guard: clamp invalid inputs to 0
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) {
    return "0:00";
  }

  // Integer floor — no floating-point accumulation across ticks
  const s = Math.floor(totalSeconds);

  // Tier 1: < 1 hour  mm:ss
  if (s < SECONDS_PER_HOUR) {
    const minutes = Math.floor(s / SECONDS_PER_MINUTE);
    const seconds = s % SECONDS_PER_MINUTE;
    return `${minutes}:${pad2(seconds)}`;
  }

  // Tier 2: < 1 day  h:mm:ss
  if (s < SECONDS_PER_DAY) {
    const hours = Math.floor(s / SECONDS_PER_HOUR);
    const remaining = s % SECONDS_PER_HOUR;
    const minutes = Math.floor(remaining / SECONDS_PER_MINUTE);
    const seconds = remaining % SECONDS_PER_MINUTE;
    return `${hours}:${pad2(minutes)}:${pad2(seconds)}`;
  }

  // Tier 3 & 4: ≥ 1 day  use day/year labels
  // Day counting starts at 1 (Day 1 = first 24 hours), so we add 1
  // But: Day 1 ends at 86400s, Day 2 starts at 86400s
  // So: day = floor(s / 86400) + 1
  const years = Math.floor(s / SECONDS_PER_YEAR);
  const remainingAfterYears = s % SECONDS_PER_YEAR;
  const dayIndex = Math.floor(remainingAfterYears / SECONDS_PER_DAY);
  // Day number within the year: 1-indexed (Day 1 through Day 365)
  const dayNumber = dayIndex + 1;
  const remainingInDay = remainingAfterYears % SECONDS_PER_DAY;
  const hours = Math.floor(remainingInDay / SECONDS_PER_HOUR);
  const minutes = Math.floor(
    (remainingInDay % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE,
  );
  const timeStr = `${hours}:${pad2(minutes)}`;

  // Tier 4: ≥ 1 year  Year N · Day N · h:mm
  if (years >= 1) {
    return `Year ${years + 1} · Day ${dayNumber} · ${timeStr}`;
  }

  // Tier 3: < 1 year  Day N · h:mm
  return `Day ${dayNumber} · ${timeStr}`;
}

/**
 * Pad a number to 2 digits with leading zero.
 * Inlined for performance (no string template overhead).
 */
function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}
